import React, { useEffect, useState, useRef, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowUpRight } from '@phosphor-icons/react';
interface CaseStudy {
  title: string;
  slug: string;
  image: string;
  category: string;
  year: string;
  whyItMatters: string;
  metric: string;
  metricLabel: string;
}
const caseStudies: CaseStudy[] = [{
  title: 'Faculty Information System',
  slug: 'case-western',
  image: 'https://cdn.magicpatterns.com/uploads/r6QmYhNhTgdAoSYhnocL3u/CWRU_COMPV.png',
  category: 'AI EXPLORATION',
  year: '2024',
  whyItMatters: 'Led a 2-week, 16-interview research sprint and synthesized findings with affinity mapping, then designed AI CV parsing with 85%+ accuracy that cut faculty data entry from 30 minutes to seconds and secured an Amazon pilot.',
  metric: '30 min → seconds',
  metricLabel: 'Faculty CV entry time · 85%+ AI parsing accuracy · secured Amazon pilot'
}, {
  title: 'U.S. Bank Commercial Card',
  slug: 'us-bank',
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/cGTn1zWyBRLJq9z8XJ288f/usbthumb.png',
  category: 'FINANCIAL SERVICES',
  year: '2022-2024',
  whyItMatters: 'Redesigned the corporate One Card onboarding end to end, building 20 reusable components and cutting processing from 54 to 20 days, reaching 98% satisfaction, 96% digital adoption, and 20,000 hours saved annually.',
  metric: '98% satisfaction',
  metricLabel: '54→20 day onboarding · 96% digital adoption · 20,000 hours saved yearly'
}, {
  title: 'Unisys Stealth',
  slug: 'cyber-security',
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/odCcwZqxVnrHSvVy5xeJrh/unisys_thumb.png',
  category: 'DIGITAL PROTECTION',
  year: '2021-2022',
  whyItMatters: 'Redesigned Unisys Stealth\u2019s micro-segmentation and threat-analysis tools at platform scale, translating dense security data into clear visualizations and a calm, scannable workflow analysts can navigate confidently during a high-pressure shift.',
  metric: 'Complex → intuitive',
  metricLabel: 'Platform-wide redesign of micro-segmentation & threat-analysis tooling'
}, {
  title: 'Conversational AI',
  slug: 'ai-comparison',
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/d2sojXsLucASkJnUpLu1Ks/Gemini_Generated_Image_qq6ptsqq6ptsqq6p.png',
  category: 'AI COMPARISON',
  year: '2020-2021',
  whyItMatters: 'Benchmarked Watson, Dialogflow, Microsoft, and Amazon, then designed natural dialogue patterns and built working \u201cTrack Status\u201d and \u201cFee Lookup\u201d demos that drove the platform decision and reduced call-center volume.',
  metric: '4 platforms benchmarked',
  metricLabel: 'Drove the conversational-AI platform decision · reduced call volume'
}];
export function ParallaxCaseStudies() {
  const navigate = useNavigate();
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [visibleItems, setVisibleItems] = useState<boolean[]>([false, false, false, false]);
  useEffect(() => {
    const currentSection = sectionRef.current;
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          caseStudies.forEach((_, index) => {
            setTimeout(() => {
              setVisibleItems(prev => {
                const newState = [...prev];
                newState[index] = true;
                return newState;
              });
            }, index * 120);
          });
        }
      });
    }, {
      threshold: 0.05,
      rootMargin: '0px 0px -40px 0px'
    });
    if (currentSection) {
      observer.observe(currentSection);
    }
    return () => {
      if (currentSection) {
        observer.unobserve(currentSection);
      }
    };
  }, []);
  const handleCardClick = (slug: string) => {
    sessionStorage.setItem('navigatedFromHome', 'true');
    sessionStorage.removeItem('cameFromChat');
    window.scrollTo({
      top: 0,
      behavior: 'instant'
    });
    navigate(`/case-study/${slug}`);
  };
  return <section ref={sectionRef} className="bg-white pt-24 sm:pt-32 md:pt-40 pb-24 sm:pb-32 md:pb-40 px-6 sm:px-12 md:px-16 lg:px-24 overflow-hidden" id="projects" data-id="element-1731">
      <div className="container mx-auto max-w-7xl flex flex-col" data-id="element-1732">
        {/* Unified Section Header */}
        <div className="mb-16 sm:mb-24" style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
        transition: 'opacity 0.8s ease-out, transform 0.8s ease-out'
      }} data-id="element-1733">
          <div className="text-xs tracking-[0.3em] uppercase text-slate-500 font-medium mb-4" style={{
          fontFamily: "'Ronzino', sans-serif"
        }} data-id="element-1734">
            SELECTED WORK
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-light text-slate-900 leading-[1.05] tracking-tight mb-6" style={{
          fontFamily: "'Gefika', sans-serif"
        }} data-id="element-1735">
            Case Studies
          </h2>
          <p className="text-base sm:text-lg text-slate-600 leading-relaxed mb-6 max-w-2xl" style={{
          fontFamily: 'system-ui, -apple-system, sans-serif'
        }} data-id="element-1736">
            My two latest case studies are under NDA review: AI projects in
            medical and pharmacy at Eli Lilly and AArete. I'll add them once
            cleared.
          </p>
          <div className="w-12 h-px bg-slate-900" data-id="element-1737"></div>
        </div>

        {/* Amira featured case study, placed after the established work */}
        <div className="order-2 mt-12 sm:mt-16" style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
        transition: 'opacity 0.8s ease-out 0.1s, transform 0.8s ease-out 0.1s'
      }} data-id="element-1738">
          <div className="relative w-full text-left bg-slate-900 border border-white/15 rounded-md overflow-hidden" data-id="element-1739">
            <div className="grid grid-cols-1 md:grid-cols-2 items-stretch" data-id="element-1740">
              {/* Featured image */}
              <div className="relative aspect-[4/3] md:aspect-auto bg-slate-900 overflow-hidden" data-id="element-1741">
                <img src="https://cdn.magicpatterns.com/uploads/x4S7EPV4CN7QuEvhUbsnuF/mockuuups-macbook-pro-16-inch-mockup-on-a-textured-gray-surface-with-dramatic-lighting.jpg" alt="Amira AI agent harness shown on a MacBook Pro" className="absolute inset-0 w-full h-full object-cover" data-id="element-1742" />
                <div className="absolute top-4 left-4 sm:top-6 sm:left-6" data-id="element-1743">
                  <span className="inline-flex items-center gap-1.5 bg-slate-900/85 backdrop-blur-sm text-white text-[10px] tracking-[0.2em] uppercase font-medium px-3 py-1.5 rounded-full" style={{
                  fontFamily: "'Ronzino', sans-serif"
                }} data-id="element-1744">
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-400" data-id="element-1745" />
                    Releasing end of July
                  </span>
                </div>
              </div>

              {/* Content side */}
              <div className="p-6 sm:p-8 md:p-10 lg:p-12 flex flex-col justify-center" data-id="element-1746">
                <div className="text-[10px] tracking-[0.25em] uppercase text-white/50 font-medium mb-3" style={{
                fontFamily: "'Ronzino', sans-serif"
              }} data-id="element-1747">
                  PORTABLE AGENT HARNESS
                </div>
                <h3 className="text-2xl sm:text-3xl md:text-4xl font-light text-white leading-tight mb-4" style={{
                fontFamily: "'Gefika', sans-serif"
              }} data-id="element-1748">
                  Amira
                </h3>

                <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-blue-300 font-medium mb-5" style={{
                fontFamily: "'Ronzino', sans-serif"
              }} data-id="element-1749">
                  Standalone, connects into Silo
                </div>

                <p className="text-sm sm:text-base text-white/70 leading-relaxed mb-6" data-id="element-1750">
                  Amira grew out of Context, my original idea for an AI that
                  observes how teams really work and helps them improve without
                  taking over. It is now a proprietary, portable harness you can
                  use on its own to design, learn how to work with AI, build
                  agents, and create sites or even companies, and it connects
                  into the Silo backend we have been building for where digital
                  building is going. The full demo and product story are inside
                  the case study.
                </p>

                {/* Capability tags */}
                <div className="flex flex-wrap gap-2 mb-6" data-id="element-1751">
                  {['Next.js + Python', 'Browser automation', 'Persistent memory', 'Dream Engine', 'Model routing'].map(tag => <span key={tag} className="text-[10px] tracking-[0.15em] uppercase text-white/60 border border-white/15 rounded-full px-3 py-1.5" style={{
                  fontFamily: "'Ronzino', sans-serif"
                }} data-id="element-1752">
                      {tag}
                    </span>)}
                </div>

                {/* Read the case study */}
                <button onClick={() => handleCardClick('amira')} className="group/link inline-flex items-center gap-1.5 self-start text-xs uppercase tracking-[0.2em] text-white font-medium mb-6 hover:text-blue-300 transition-colors" style={{
                fontFamily: "'Ronzino', sans-serif"
              }} data-id="element-1753">
                  Read the case study
                  <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5" weight="bold" data-id="element-1754" />
                </button>

                {/* Authorship + access */}
                <div className="flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-white/50 font-medium pt-5 border-t border-white/10" style={{
                fontFamily: "'Ronzino', sans-serif"
              }} data-id="element-1755">
                  <span className="text-white/70" data-id="element-1756">Built by Mateo</span>
                  <span className="text-white/20" data-id="element-1757">&middot;</span>
                  <span data-id="element-1758">Releasing end of July</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Equal-sized 2x2 grid */}
        <div className="order-1 grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-6 items-stretch" data-id="element-1759">
          {caseStudies.map((study, index) => <div key={study.slug} className="h-full" style={{
          opacity: visibleItems[index] ? 1 : 0,
          transform: visibleItems[index] ? 'translateY(0)' : 'translateY(30px)',
          transition: `opacity 0.8s ease-out ${index * 0.08}s, transform 0.8s ease-out ${index * 0.08}s`
        }} data-id="element-1760">
              <button onClick={() => handleCardClick(study.slug)} aria-label={`View case study: ${study.title}`} className="group relative flex flex-col w-full h-full text-left bg-slate-50 border border-slate-200 rounded-md overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2 transition-all duration-300 hover:border-slate-300" data-id="element-1761">
                {/* Image fills tile */}
                <div className="relative w-full aspect-[4/3] sm:aspect-[16/10] overflow-hidden bg-slate-100" data-id="element-1762">
                  <img src={study.image} alt={study.title} className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]" style={{
                objectPosition: study.slug === 'us-bank' || study.slug === 'cyber-security' ? 'top' : 'center'
              }} data-id="element-1763" />
                  {/* Top-right View project hint (hover) */}
                  <div className="absolute top-4 right-4 sm:top-5 sm:right-5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1764">
                    <span className="inline-flex items-center gap-1.5 bg-slate-900 text-white text-[10px] tracking-[0.2em] uppercase font-medium px-3 py-1.5 rounded-full" style={{
                  fontFamily: "'Ronzino', sans-serif"
                }} data-id="element-1765">
                      View project
                      <ArrowUpRight className="w-3 h-3" weight="bold" data-id="element-1766" />
                    </span>
                  </div>
                </div>

                {/* Bottom content block */}
                <div className="p-6 sm:p-8 md:p-10" data-id="element-1767">
                  <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-medium mb-3" style={{
                fontFamily: "'Ronzino', sans-serif"
              }} data-id="element-1768">
                    {study.category}
                  </div>
                  <h3 className="text-xl sm:text-2xl md:text-3xl font-light text-slate-900 leading-tight mb-5" style={{
                fontFamily: "'Gefika', sans-serif"
              }} data-id="element-1769">
                    {study.title}
                  </h3>

                  {/* Design Impact */}
                  <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-medium mb-2" style={{
                fontFamily: "'Ronzino', sans-serif"
              }} data-id="element-1770">
                    Design Impact
                  </div>
                  <p className="text-sm sm:text-base text-slate-700 leading-relaxed mb-6" style={{
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }} data-id="element-1771">
                    {study.whyItMatters}
                  </p>

                  {/* Impact callout */}
                  <div className="pt-5 border-t border-slate-200" data-id="element-1772">
                    <p className="text-xs sm:text-sm font-semibold text-slate-900 leading-snug" style={{
                  fontFamily: 'system-ui, -apple-system, sans-serif'
                }} data-id="element-1773">
                      {study.metricLabel}
                    </p>
                  </div>
                </div>
              </button>
            </div>)}
        </div>
      </div>
    </section>;
}